// Help.js

import React, { useState } from "react";

import FeedHelp from "../help/FeedHelp";
import ChatHelp from "../help/ChatHelp";
import P2PHelp from "../help/p2pHelp";
import AssetHelp from "../help/AssetHelp";
import SettingsHelp from "../help/settingsHelp";

/* =========================================================
   HELP CENTER
========================================================= */

export function Help({ onBack }) {

  const [page, setPage] = useState("home");

  /* =====================================================
     SUB-PAGE CONFIG
  ====================================================== */

  const subPages = {
    feed:     { title: "Feed Help",     Component: FeedHelp },
    chat:     { title: "Chat Help",     Component: ChatHelp },
    p2p:      { title: "P2P Help",      Component: P2PHelp },
    assets:   { title: "Assets Help",   Component: AssetHelp },
    settings: { title: "Settings Help", Component: SettingsHelp },
  };

  /* =====================================================
     SUB-PAGES (FEED / CHAT / P2P / ASSETS / SETTINGS)
  ====================================================== */

  if (subPages[page]) {

    const { title, Component } = subPages[page];

    return (

      <div className="flex flex-col h-full bg-white dark:bg-neutral-900">

        {/* ================= HEADER ================= */}

        <div className="sticky top-0 bg-white dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-700 px-2 py-3 flex items-center gap-2">

          <button
            onClick={() => setPage("home")}
            className="flex items-center justify-center w-9 h-9 shrink-0 rounded-full text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800 active:opacity-60 touch-manipulation transition-colors"
            aria-label="Back"
          >
            <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
              <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
            </svg>
          </button>

          <h1 className="text-lg font-semibold">
            {title}
          </h1>

        </div>

        {/* ================= CONTENT ================= */}

        <div className="flex-1 overflow-y-auto px-5 py-6">

          <Component />

        </div>

      </div>

    );

  }

  /* =====================================================
     HOME
  ====================================================== */

  return (

    <div className="flex flex-col h-full bg-white dark:bg-neutral-900">

      {/* ================= HEADER ================= */}

      <div className="sticky top-0 bg-white dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-700 px-2 py-3 flex items-center gap-2">

        {onBack && (

          <button
            onClick={onBack}
            className="flex items-center justify-center w-9 h-9 shrink-0 rounded-full text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800 active:opacity-60 touch-manipulation transition-colors"
            aria-label="Back"
          >
            <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
              <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
            </svg>
          </button>

        )}

        <h1 className="text-lg font-semibold">
          Help Center
        </h1>

      </div>

      {/* ================= CONTENT ================= */}

      <div className="flex-1 overflow-y-auto px-5 py-6 space-y-6">

        {/* =======================================================
            FEED
        ======================================================== */}

        <section>

          <button
            onClick={() => setPage("feed")}
            className="
              w-full
              rounded-lg
              border
              border-neutral-200
              dark:border-neutral-700
              p-4
              text-left
              hover:bg-neutral-50
              dark:hover:bg-neutral-800
              transition
            "
          >

            <div className="flex items-center justify-between">

              <div>

                <h2 className="font-semibold text-base">
                  Feed
                </h2>

                <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-400">
                  Learn how to create posts, edit posts,
                  delete posts, repost content, support
                  creators and report posts.
                </p>

              </div>

              <span className="text-xl">
                ›
              </span>

            </div>

          </button>

        </section>

        {/* =======================================================
            CHATS
        ======================================================== */}

        <section>

          <button
            onClick={() => setPage("chat")}
            className="
              w-full
              rounded-lg
              border
              border-neutral-200
              dark:border-neutral-700
              p-4
              text-left
              hover:bg-neutral-50
              dark:hover:bg-neutral-800
              transition
            "
          >

            <div className="flex items-center justify-between">

              <div>

                <h2 className="font-semibold text-base">
                  Chats
                </h2>

                <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-400">
                  Send direct messages, create group chats,
                  share media, and manage conversations.
                </p>

              </div>

              <span className="text-xl">
                ›
              </span>

            </div>

          </button>

        </section>

        {/* =======================================================
            ASSETS
        ======================================================== */}

        <section>

          <button
            onClick={() => setPage("assets")}
            className="
              w-full
              rounded-lg
              border
              border-neutral-200
              dark:border-neutral-700
              p-4
              text-left
              hover:bg-neutral-50
              dark:hover:bg-neutral-800
              transition
            "
          >

            <div className="flex items-center justify-between">

              <div>

                <h2 className="font-semibold text-base">
                  Assets
                </h2>

                <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-400">
                  View your balances, receive, send,
                  buy and sell supported assets.
                </p>

              </div>

              <span className="text-xl">
                ›
              </span>

            </div>

          </button>

        </section>

        {/* =======================================================
            P2P
        ======================================================== */}

        <section>

          <button
            onClick={() => setPage("p2p")}
            className="
              w-full
              rounded-lg
              border
              border-neutral-200
              dark:border-neutral-700
              p-4
              text-left
              hover:bg-neutral-50
              dark:hover:bg-neutral-800
              transition
            "
          >

            <div className="flex items-center justify-between">

              <div>

                <h2 className="font-semibold text-base">
                  P2P Marketplace
                </h2>

                <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-400">
                  Buy and sell supported assets securely
                  through the platform's order process.
                </p>

              </div>

              <span className="text-xl">
                ›
              </span>

            </div>

          </button>

        </section>

        {/* =======================================================
            SETTINGS
        ======================================================== */}

        <section>

          <button
            onClick={() => setPage("settings")}
            className="
              w-full
              rounded-lg
              border
              border-neutral-200
              dark:border-neutral-700
              p-4
              text-left
              hover:bg-neutral-50
              dark:hover:bg-neutral-800
              transition
            "
          >

            <div className="flex items-center justify-between">

              <div>

                <h2 className="font-semibold text-base">
                  Settings
                </h2>

                <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-400">
                  Manage your profile photo, username,
                  display name, theme, email, password
                  and account.
                </p>

              </div>

              <span className="text-xl">
                ›
              </span>

            </div>

          </button>

        </section>

      </div>

    </div>

  );

}

export default Help;